import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { 
  Users, 
  Clock,
  PoundSterling,
  Plus,
  Edit2,
  Trash2,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  UserPlus,
  AlertCircle,
  StickyNote,
  Sparkles,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  UserMinus,
  Archive,
  RotateCcw,
  Filter,
  Repeat,
  Wallet,
  CreditCard,
  History,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, startOfWeek, endOfWeek, eachDayOfInterval, parseISO, isToday, isSameDay } from 'date-fns';

const StaffPage = () => {
  const { currentLocation, api } = useAuth();
  
  // State
  const [employees, setEmployees] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [dailyCost, setDailyCost] = useState([]);
  const [staffSummary, setStaffSummary] = useState(null);
  const [employeeNotes, setEmployeeNotes] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [view, setView] = useState('schedule'); // 'schedule' | 'employees' | 'costs' | 'payments'
  
  // Week navigation
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  
  // Dialogs
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [debtDialogOpen, setDebtDialogOpen] = useState(false);
  const [selectedEmployeeForPayment, setSelectedEmployeeForPayment] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [deleteEmployeeDialogOpen, setDeleteEmployeeDialogOpen] = useState(false);
  const [transactionHistoryOpen, setTransactionHistoryOpen] = useState(false);
  const [selectedEmployeeTransactions, setSelectedEmployeeTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [shiftToDelete, setShiftToDelete] = useState(null);
  const [selectedEmployeeForNote, setSelectedEmployeeForNote] = useState(null);
  const [selectedEmployeeForStatus, setSelectedEmployeeForStatus] = useState(null);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  
  // Form state
  const [shiftForm, setShiftForm] = useState({
    employee_id: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    start_time: '09:00',
    end_time: '17:00',
    break_minutes: 30,
    notes: '',
    repeat: false,
    repeat_weeks: 4
  });
  
  const [employeeForm, setEmployeeForm] = useState({
    name: '',
    role: '',
    hourly_rate: ''
  });
  
  const [noteContent, setNoteContent] = useState('');
  const [statusForm, setStatusForm] = useState({
    status: 'left',
    left_date: format(new Date(), 'yyyy-MM-dd')
  });
  
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_date: format(new Date(), 'yyyy-MM-dd'),
    notes: ''
  });
  
  const [debtForm, setDebtForm] = useState({
    amount: '',
    description: '',
    date_incurred: format(new Date(), 'yyyy-MM-dd')
  });

  // Computed week dates (memoized to avoid infinite loops)
  const weekEnd = useMemo(() => endOfWeek(currentWeekStart, { weekStartsOn: 1 }), [currentWeekStart]);
  
  const weekDates = useMemo(() => eachDayOfInterval({
    start: currentWeekStart,
    end: weekEnd
  }), [currentWeekStart, weekEnd]);

  // Fetch all data
  useEffect(() => {
    const fetchData = async () => {
      if (!currentLocation?.id) return;
      
      setLoading(true);
      const locationId = currentLocation.id;
      const startDate = format(currentWeekStart, 'yyyy-MM-dd');
      const endDate = format(weekEnd, 'yyyy-MM-dd');
      
      try {
        const [empRes, schedRes, costRes, summaryRes, notesRes] = await Promise.all([
          api.get('/employees', { params: { location_id: locationId, include_inactive: showInactive } }),
          api.get('/schedule', { params: { location_id: locationId, start_date: startDate, end_date: endDate } }),
          api.get('/schedule/daily-cost', { params: { location_id: locationId, start_date: startDate, end_date: endDate } }).catch(() => ({ data: [] })),
          api.get('/staff/summary', { params: { location_id: locationId } }).catch(() => ({ data: null })),
          api.get('/notes', { params: { location_id: locationId, entity_type: 'employee' } }).catch(() => ({ data: [] }))
        ]);
        
        setEmployees(empRes.data);
        setSchedule(schedRes.data);
        setDailyCost(costRes.data || []);
        setStaffSummary(summaryRes.data);
        setEmployeeNotes(notesRes.data);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [currentLocation?.id, currentWeekStart, weekEnd, api, showInactive]);
  
  // Refetch functions for after mutations
  const refetchScheduleData = async () => {
    if (!currentLocation?.id) return;
    const startDate = format(currentWeekStart, 'yyyy-MM-dd');
    const endDate = format(weekEnd, 'yyyy-MM-dd');
    
    try {
      const [schedRes, costRes] = await Promise.all([
        api.get('/schedule', { params: { location_id: currentLocation.id, start_date: startDate, end_date: endDate } }),
        api.get('/schedule/daily-cost', { params: { location_id: currentLocation.id, start_date: startDate, end_date: endDate } })
      ]);
      setSchedule(schedRes.data);
      setDailyCost(costRes.data || []);
    } catch (err) {
      console.error('Error refetching schedule:', err);
    }
  };
  
  const refetchEmployees = async () => {
    if (!currentLocation?.id) return;
    try {
      const [empRes, summaryRes, notesRes] = await Promise.all([
        api.get('/employees', { params: { location_id: currentLocation.id, include_inactive: showInactive } }),
        api.get('/staff/summary', { params: { location_id: currentLocation.id } }),
        api.get('/notes', { params: { location_id: currentLocation.id, entity_type: 'employee' } })
      ]);
      setEmployees(empRes.data);
      setStaffSummary(summaryRes.data);
      setEmployeeNotes(notesRes.data);
    } catch (err) {
      console.error('Error refetching employees:', err);
    }
  };
  
  const fetchPaymentSummary = async () => {
    if (!currentLocation?.id) return;
    try {
      const res = await api.get('/payments/summary', { params: { location_id: currentLocation.id, include_inactive: showInactive } });
      setPaymentSummary(res.data);
    } catch (err) {
      console.error('Error fetching payment summary:', err);
    }
  };

  const [hoursDashboard, setHoursDashboard] = useState(null);
  const [loadingHours, setLoadingHours] = useState(false);

  const fetchHoursDashboard = async () => {
    if (!currentLocation?.id) return;
    setLoadingHours(true);
    try {
      const res = await api.get('/staff/hours-dashboard', { params: { location_id: currentLocation.id } });
      setHoursDashboard(res.data);
    } catch (err) {
      console.error('Error fetching hours dashboard:', err);
    } finally {
      setLoadingHours(false);
    }
  };
  
  // Fetch payment data when switching to payments view
  useEffect(() => {
    if (view === 'payments' && currentLocation?.id) {
      fetchPaymentSummary();
      fetchHoursDashboard();
    }
  }, [view, currentLocation?.id, showInactive]);

  // Week navigation
  const prevWeek = () => setCurrentWeekStart(addDays(currentWeekStart, -7));
  const nextWeek = () => setCurrentWeekStart(addDays(currentWeekStart, 7));
  const goToToday = () => setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));

  // Payment functions
  const openPaymentDialog = (emp) => {
    setSelectedEmployeeForPayment(emp);
    setPaymentForm({
      amount: emp.balance_owed > 0 ? emp.balance_owed.toFixed(2) : '',
      payment_date: format(new Date(), 'yyyy-MM-dd'),
      notes: ''
    });
    setPaymentDialogOpen(true);
  };
  
  const openDebtDialog = (emp) => {
    setSelectedEmployeeForPayment(emp);
    setDebtForm({
      amount: '',
      description: '',
      date_incurred: format(new Date(), 'yyyy-MM-dd')
    });
    setDebtDialogOpen(true);
  };
  
  const recordPayment = async () => {
    if (!selectedEmployeeForPayment || !paymentForm.amount) {
      toast.error('Please enter an amount');
      return;
    }
    
    try {
      await api.post('/payments', {
        employee_id: selectedEmployeeForPayment.employee_id,
        amount: parseFloat(paymentForm.amount),
        payment_date: paymentForm.payment_date,
        notes: paymentForm.notes || null
      });
      toast.success(`Payment of ${formatCurrency(paymentForm.amount)} recorded`);
      setPaymentDialogOpen(false);
      fetchPaymentSummary();
    } catch (err) {
      console.error('Error recording payment:', err);
      toast.error('Failed to record payment');
    }
  };
  
  const addHistoricDebt = async () => {
    if (!selectedEmployeeForPayment || !debtForm.amount || !debtForm.description) {
      toast.error('Please enter amount and description');
      return;
    }
    
    try {
      await api.post('/payments/historic-debt', {
        employee_id: selectedEmployeeForPayment.employee_id,
        amount: parseFloat(debtForm.amount),
        description: debtForm.description,
        date_incurred: debtForm.date_incurred
      });
      toast.success('Historic debt added');
      setDebtDialogOpen(false);
      fetchPaymentSummary();
    } catch (err) {
      console.error('Error adding debt:', err);
      toast.error('Failed to add historic debt');
    }
  };

  // Transaction History handlers
  const openTransactionHistory = async (employee) => {
    setSelectedEmployeeForPayment(employee);
    setLoadingTransactions(true);
    setTransactionHistoryOpen(true);
    
    try {
      const res = await api.get(`/employees/${employee.employee_id}/transactions`);
      setSelectedEmployeeTransactions(res.data);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      toast.error('Failed to load transaction history');
      setSelectedEmployeeTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    try {
      await api.delete(`/payments/${transactionId}`);
      toast.success('Transaction deleted');
      // Refresh the transaction list
      if (selectedEmployeeForPayment) {
        const res = await api.get(`/employees/${selectedEmployeeForPayment.employee_id}/transactions`);
        setSelectedEmployeeTransactions(res.data);
      }
      // Refresh payment summary
      fetchPaymentSummary();
    } catch (err) {
      console.error('Error deleting transaction:', err);
      toast.error('Failed to delete transaction');
    }
  };

  // Shift CRUD
  const openAddShift = (date = null, employeeId = null) => {
    setEditingShift(null);
    setShiftForm({
      employee_id: employeeId || '',
      date: date || format(new Date(), 'yyyy-MM-dd'),
      start_time: '09:00',
      end_time: '17:00',
      break_minutes: 30,
      notes: '',
      repeat: false,
      repeat_weeks: 4
    });
    setShiftDialogOpen(true);
  };

  const openEditShift = (shift) => {
    setEditingShift(shift);
    setShiftForm({
      employee_id: shift.employee_id,
      date: shift.date,
      start_time: shift.start_time,
      end_time: shift.end_time,
      break_minutes: shift.break_minutes || 0,
      notes: shift.notes || '',
      repeat: false,
      repeat_weeks: 4
    });
    setShiftDialogOpen(true);
  };

  const saveShift = async () => {
    if (!shiftForm.employee_id || !shiftForm.date) {
      toast.error('Please select an employee and date');
      return;
    }

    try {
      if (editingShift) {
        await api.put(`/schedule/${editingShift.id}`, {
          start_time: shiftForm.start_time,
          end_time: shiftForm.end_time,
          break_minutes: parseInt(shiftForm.break_minutes) || 0,
          notes: shiftForm.notes
        });
        toast.success('Shift updated');
      } else {
        // Handle repeating shifts
        if (shiftForm.repeat && shiftForm.repeat_weeks > 1) {
          await api.post('/schedule/repeat', {
            location_id: currentLocation.id,
            employee_id: shiftForm.employee_id,
            start_date: shiftForm.date,
            start_time: shiftForm.start_time,
            end_time: shiftForm.end_time,
            break_minutes: parseInt(shiftForm.break_minutes) || 0,
            notes: shiftForm.notes,
            repeat_weeks: parseInt(shiftForm.repeat_weeks)
          });
          toast.success(`Created ${shiftForm.repeat_weeks} weekly shifts`);
        } else {
          await api.post('/schedule', {
            location_id: currentLocation.id,
            employee_id: shiftForm.employee_id,
            date: shiftForm.date,
            start_time: shiftForm.start_time,
            end_time: shiftForm.end_time,
            break_minutes: parseInt(shiftForm.break_minutes) || 0,
            notes: shiftForm.notes
          });
          toast.success('Shift added');
        }
      }
      setShiftDialogOpen(false);
      refetchScheduleData();
    } catch (err) {
      console.error('Error saving shift:', err);
      toast.error(err.response?.data?.detail || 'Failed to save shift');
    }
  };

  const confirmDeleteShift = (shift) => {
    setShiftToDelete(shift);
    setDeleteDialogOpen(true);
  };

  const deleteShift = async () => {
    if (!shiftToDelete) return;
    try {
      await api.delete(`/schedule/${shiftToDelete.id}`);
      toast.success('Shift deleted');
      setDeleteDialogOpen(false);
      setShiftToDelete(null);
      refetchScheduleData();
    } catch (err) {
      console.error('Error deleting shift:', err);
      toast.error('Failed to delete shift');
    }
  };

  // Employee CRUD
  const openAddEmployee = () => {
    setEditingEmployee(null);
    setEmployeeForm({ name: '', role: '', hourly_rate: '' });
    setEmployeeDialogOpen(true);
  };

  const openEditEmployee = (emp) => {
    setEditingEmployee(emp);
    setEmployeeForm({
      name: emp.name,
      role: emp.role || emp.job_title || '',
      hourly_rate: emp.hourly_rate?.toString() || ''
    });
    setEmployeeDialogOpen(true);
  };

  const saveEmployee = async () => {
    if (!employeeForm.name.trim()) {
      toast.error('Please enter a name');
      return;
    }

    try {
      const payload = {
        name: employeeForm.name.trim(),
        role: employeeForm.role.trim() || null,
        hourly_rate: employeeForm.hourly_rate ? parseFloat(employeeForm.hourly_rate) : null
      };

      if (editingEmployee) {
        await api.put(`/employees/${editingEmployee.id}`, payload);
        toast.success('Employee updated');
      } else {
        await api.post('/employees', {
          ...payload,
          location_id: currentLocation.id
        });
        toast.success('Employee added');
      }
      setEmployeeDialogOpen(false);
      refetchEmployees();
    } catch (err) {
      console.error('Error saving employee:', err);
      toast.error(err.response?.data?.detail || 'Failed to save employee');
    }
  };

  // Note functions
  const openAddNote = (emp) => {
    setSelectedEmployeeForNote(emp);
    setNoteContent('');
    setNoteDialogOpen(true);
  };

  const saveNote = async () => {
    if (!noteContent.trim() || !selectedEmployeeForNote) {
      toast.error('Please enter note content');
      return;
    }

    try {
      await api.post('/notes', {
        location_id: currentLocation.id,
        content: noteContent.trim(),
        entity_type: 'employee',
        entity_id: selectedEmployeeForNote.id,
        source: 'manual'
      });
      toast.success('Note saved');
      setNoteDialogOpen(false);
      setNoteContent('');
      setSelectedEmployeeForNote(null);
      refetchEmployees();
    } catch (err) {
      console.error('Error saving note:', err);
      toast.error('Failed to save note');
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await api.delete(`/notes/${noteId}`);
      toast.success('Note deleted');
      refetchEmployees();
    } catch (err) {
      console.error('Error deleting note:', err);
      toast.error('Failed to delete note');
    }
  };

  // Employee status functions
  const openStatusDialog = (emp) => {
    setSelectedEmployeeForStatus(emp);
    setStatusForm({
      status: 'left',
      left_date: format(new Date(), 'yyyy-MM-dd')
    });
    setStatusDialogOpen(true);
  };

  const changeEmployeeStatus = async () => {
    if (!selectedEmployeeForStatus) return;
    
    try {
      await api.put(`/employees/${selectedEmployeeForStatus.id}/status`, {
        status: statusForm.status,
        left_date: statusForm.status === 'left' ? statusForm.left_date : null
      });
      
      const statusLabel = {
        'left': 'marked as left',
        'archived': 'archived',
        'active': 'reactivated'
      };
      
      toast.success(`${selectedEmployeeForStatus.name} ${statusLabel[statusForm.status]}`);
      setStatusDialogOpen(false);
      setSelectedEmployeeForStatus(null);
      refetchEmployees();
    } catch (err) {
      console.error('Error changing status:', err);
      toast.error('Failed to update employee status');
    }
  };

  const reactivateEmployee = async (emp) => {
    try {
      await api.put(`/employees/${emp.id}/status`, { status: 'active' });
      toast.success(`${emp.name} reactivated`);
      refetchEmployees();
    } catch (err) {
      console.error('Error reactivating:', err);
      toast.error('Failed to reactivate employee');
    }
  };

  const openDeleteEmployeeDialog = (emp) => {
    setEmployeeToDelete(emp);
    setDeleteEmployeeDialogOpen(true);
  };

  const deleteEmployeePermanently = async () => {
    if (!employeeToDelete) return;
    
    try {
      await api.delete(`/employees/${employeeToDelete.id}`);
      toast.success(`${employeeToDelete.name} deleted permanently`);
      setDeleteEmployeeDialogOpen(false);
      setEmployeeToDelete(null);
      setExpandedEmployee(null);
      refetchEmployees();
    } catch (err) {
      console.error('Error deleting employee:', err);
      toast.error('Failed to delete employee');
    }
  };

  // Get notes for a specific employee
  const getNotesForEmployee = (employeeId) => {
    return employeeNotes.filter(n => n.entity_id === employeeId);
  };

  // Get shifts for a specific day and employee
  const getShiftsForCell = (date, employeeId) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return schedule.filter(s => s.date === dateStr && s.employee_id === employeeId);
  };

  // Get daily cost data for a date
  const getDailyCostForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return dailyCost.find(d => d.date === dateStr);
  };

  // Format currency
  const formatCurrency = (val) => `£${(val || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  
  // Format date for notes
  const formatNoteDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Employee status helpers
  const activeEmployees = employees.filter(e => !e.status || e.status === 'active');
  const inactiveEmployees = employees.filter(e => e.status === 'left' || e.status === 'archived');
  
  const getStatusBadge = (emp) => {
    if (emp.status === 'left') {
      return (
        <Badge variant="outline" className="text-xs border-orange-500 text-orange-600">
          Left {emp.left_date ? formatNoteDate(emp.left_date) : ''}
        </Badge>
      );
    }
    if (emp.status === 'archived') {
      return (
        <Badge variant="outline" className="text-xs border-gray-500 text-gray-600">
          Archived
        </Badge>
      );
    }
    return null;
  };

  // Calculate week totals
  const weekTotalHours = dailyCost.reduce((sum, d) => sum + (d.total_hours || 0), 0);
  const weekTotalCost = dailyCost.reduce((sum, d) => sum + (d.projected_labor_cost || 0), 0);
  const weekTotalShifts = dailyCost.reduce((sum, d) => sum + (d.shifts_scheduled || 0), 0);
  
  // Count total employee notes
  const totalEmployeeNotes = employeeNotes.length;

  if (loading) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6" data-testid="staff-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Staff</h1>
          <p className="text-muted-foreground">Manage employees and schedules</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openAddEmployee} data-testid="add-employee-btn">
            <UserPlus className="w-4 h-4 mr-2" />
            Add Employee
          </Button>
          <Button onClick={() => openAddShift()} data-testid="add-shift-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Shift
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-hover">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Users className="w-4 h-4" />
              Employees
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{employees.length}</div>
            <p className="text-sm text-muted-foreground">Active staff</p>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CalendarDays className="w-4 h-4" />
              This Week
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{weekTotalShifts}</div>
            <p className="text-sm text-muted-foreground">Shifts scheduled</p>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Hours
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{weekTotalHours.toFixed(1)}</div>
            <p className="text-sm text-muted-foreground">Total this week</p>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <PoundSterling className="w-4 h-4" />
              Projected Cost
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-loss">{formatCurrency(weekTotalCost)}</div>
            <p className="text-sm text-muted-foreground">Labour this week</p>
          </CardContent>
        </Card>
      </div>

      {/* View Toggle */}
      <div className="flex gap-2 border-b pb-4 overflow-x-auto">
        <Button 
          variant={view === 'schedule' ? 'default' : 'ghost'} 
          size="sm"
          onClick={() => setView('schedule')}
          data-testid="view-schedule-btn"
          className="shrink-0"
        >
          <CalendarDays className="w-4 h-4 mr-2" />
          <span className="hidden sm:inline">Schedule</span>
          <span className="sm:hidden">Cal</span>
        </Button>
        <Button 
          variant={view === 'employees' ? 'default' : 'ghost'} 
          size="sm"
          onClick={() => setView('employees')}
          data-testid="view-employees-btn"
          className="shrink-0"
        >
          <Users className="w-4 h-4 mr-2" />
          <span className="hidden sm:inline">Employees</span>
          <span className="sm:hidden">Staff</span>
        </Button>
        <Button 
          variant={view === 'costs' ? 'default' : 'ghost'} 
          size="sm"
          onClick={() => setView('costs')}
          data-testid="view-costs-btn"
          className="shrink-0"
        >
          <PoundSterling className="w-4 h-4 mr-2" />
          <span className="hidden sm:inline">Daily Costs</span>
          <span className="sm:hidden">Costs</span>
        </Button>
        <Button 
          variant={view === 'payments' ? 'default' : 'ghost'} 
          size="sm"
          onClick={() => setView('payments')}
          data-testid="view-payments-btn"
          className="shrink-0"
        >
          <Wallet className="w-4 h-4 mr-2" />
          <span className="hidden sm:inline">Payments</span>
          <span className="sm:hidden">Pay</span>
        </Button>
      </div>

      {/* Schedule View */}
      {view === 'schedule' && (
        <Card>
          <CardHeader className="pb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle>Weekly Schedule</CardTitle>
                <CardDescription>
                  {format(currentWeekStart, 'd MMM')} - {format(weekEnd, 'd MMM yyyy')}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={prevWeek} data-testid="prev-week-btn">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={goToToday}>
                  Today
                </Button>
                <Button variant="outline" size="icon" onClick={nextWeek} data-testid="next-week-btn">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {employees.length === 0 ? (
              <div className="py-12 text-center">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">No employees yet</h3>
                <p className="text-muted-foreground mb-4">Add employees to start scheduling shifts</p>
                <Button onClick={openAddEmployee}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Employee
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <div className="inline-block min-w-full align-middle px-4 sm:px-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[120px] sticky left-0 bg-card z-10">Employee</TableHead>
                        {weekDates.map(date => (
                          <TableHead 
                            key={date.toISOString()} 
                            className={`text-center min-w-[90px] ${isToday(date) ? 'bg-primary/10' : ''}`}
                          >
                            <div className="font-medium">{format(date, 'EEE')}</div>
                            <div className="text-xs text-muted-foreground">{format(date, 'd')}</div>
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {employees.map(emp => (
                        <TableRow key={emp.id} className="table-row-hover">
                          <TableCell className="font-medium sticky left-0 bg-card z-10">
                            <div className="truncate max-w-[100px]">{emp.name}</div>
                            <div className="text-xs text-muted-foreground truncate">{emp.role || emp.job_title || '-'}</div>
                          </TableCell>
                          {weekDates.map(date => {
                            const shifts = getShiftsForCell(date, emp.id);
                            const dateStr = format(date, 'yyyy-MM-dd');
                            return (
                              <TableCell 
                              key={date.toISOString()} 
                              className={`text-center p-1 ${isToday(date) ? 'bg-primary/5' : ''}`}
                            >
                              {shifts.length > 0 ? (
                                <div className="space-y-1">
                                  {shifts.map(shift => (
                                    <div 
                                      key={shift.id}
                                      className="bg-primary/20 rounded px-2 py-1 text-xs cursor-pointer hover:bg-primary/30 transition-colors"
                                      onClick={() => openEditShift(shift)}
                                      data-testid={`shift-${shift.id}`}
                                    >
                                      <div className="font-medium">
                                        {shift.start_time} - {shift.end_time}
                                      </div>
                                      <div className="text-muted-foreground">
                                        {shift.hours?.toFixed(1)}h
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="w-full h-12 text-muted-foreground hover:text-foreground"
                                  onClick={() => openAddShift(dateStr, emp.id)}
                                  data-testid={`add-shift-${emp.id}-${dateStr}`}
                                >
                                  <Plus className="w-4 h-4" />
                                </Button>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                    {/* Daily totals row */}
                    <TableRow className="bg-muted/50 font-medium">
                      <TableCell>Daily Total</TableCell>
                      {weekDates.map(date => {
                        const costData = getDailyCostForDate(date);
                        return (
                          <TableCell key={date.toISOString()} className="text-center">
                            {costData ? (
                              <div className="text-xs">
                                <div>{costData.total_hours?.toFixed(1)}h</div>
                                <div className="text-loss">{formatCurrency(costData.projected_labor_cost)}</div>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Employees View */}
      {view === 'employees' && (
        <Card>
          <CardHeader className="pb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle>All Employees</CardTitle>
                <CardDescription>
                  {activeEmployees.length} active{inactiveEmployees.length > 0 ? `, ${inactiveEmployees.length} inactive` : ''}
                </CardDescription>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <StickyNote className="w-4 h-4" />
                  {totalEmployeeNotes} note{totalEmployeeNotes !== 1 ? 's' : ''}
                </div>
                <Button
                  variant={showInactive ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowInactive(!showInactive)}
                  data-testid="toggle-inactive-btn"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">{showInactive ? 'Showing All' : 'Show Inactive'}</span>
                  <span className="sm:hidden">{showInactive ? 'All' : 'Filter'}</span>
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {employees.length === 0 ? (
              <div className="py-12 text-center">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">
                  {showInactive ? 'No employees found' : 'No active employees'}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {showInactive ? 'Add your first team member' : 'Add employees or show inactive staff'}
                </p>
                <div className="flex gap-2 justify-center">
                  <Button onClick={openAddEmployee}>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Employee
                  </Button>
                  {!showInactive && (
                    <Button variant="outline" onClick={() => setShowInactive(true)}>
                      <Filter className="w-4 h-4 mr-2" />
                      Show Inactive
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {employees.map(emp => {
                  const empNotes = getNotesForEmployee(emp.id);
                  const isExpanded = expandedEmployee === emp.id;
                  const isInactive = emp.status === 'left' || emp.status === 'archived';
                  
                  return (
                    <div 
                      key={emp.id} 
                      className={`border rounded-lg overflow-hidden ${isInactive ? 'opacity-75 bg-muted/30' : ''}`}
                    >
                      {/* Employee Row */}
                      <div 
                        className="flex items-center justify-between p-4 bg-card hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() => setExpandedEmployee(isExpanded ? null : emp.id)}
                        data-testid={`employee-row-${emp.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isInactive ? 'bg-muted' : 'bg-primary/10'}`}>
                            <span className={`font-semibold ${isInactive ? 'text-muted-foreground' : 'text-primary'}`}>
                              {emp.name?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          </div>
                          <div>
                            <div className="font-medium flex items-center gap-2">
                              {emp.name}
                              {getStatusBadge(emp)}
                            </div>
                            <div className="text-sm text-muted-foreground flex items-center gap-2">
                              {emp.role || emp.job_title ? (
                                <Badge variant="secondary" className="text-xs">{emp.role || emp.job_title}</Badge>
                              ) : null}
                              {emp.hourly_rate && (
                                <span className="mono-num">{formatCurrency(emp.hourly_rate)}/hr</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {empNotes.length > 0 && (
                            <Badge variant="outline" className="text-xs">
                              <MessageSquare className="w-3 h-3 mr-1" />
                              {empNotes.length}
                            </Badge>
                          )}
                          {isInactive && (
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                reactivateEmployee(emp);
                              }}
                              title="Reactivate"
                              data-testid={`reactivate-${emp.id}`}
                            >
                              <RotateCcw className="w-4 h-4 text-green-600" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              openAddNote(emp);
                            }}
                            data-testid={`add-note-${emp.id}`}
                          >
                            <StickyNote className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditEmployee(emp);
                            }}
                            data-testid={`edit-employee-${emp.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          {!isInactive && (
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                openStatusDialog(emp);
                              }}
                              title="Remove employee"
                              data-testid={`remove-employee-${emp.id}`}
                            >
                              <UserMinus className="w-4 h-4 text-orange-500" />
                            </Button>
                          )}
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                      
                      {/* Notes Section (Expandable) */}
                      {isExpanded && (
                        <div className="border-t bg-muted/30 p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-sm flex items-center gap-2">
                              <StickyNote className="w-4 h-4" />
                              Notes & Insights
                            </h4>
                            <div className="flex gap-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => openAddNote(emp)}
                                data-testid={`add-note-expanded-${emp.id}`}
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Add Note
                              </Button>
                              {isInactive ? (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="text-destructive border-destructive hover:bg-destructive/10"
                                  onClick={() => openDeleteEmployeeDialog(emp)}
                                  data-testid={`delete-employee-${emp.id}`}
                                >
                                  <Trash2 className="w-3 h-3 mr-1" />
                                  Delete Permanently
                                </Button>
                              ) : null}
                            </div>
                          </div>
                          
                          {empNotes.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-4">
                              No notes yet. Add a note or ask the AI Advisor about this employee.
                            </p>
                          ) : (
                            <div className="space-y-2">
                              {empNotes.map(note => (
                                <div 
                                  key={note.id} 
                                  className="bg-card rounded-md p-3 border group"
                                  data-testid={`note-${note.id}`}
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1">
                                      {note.source === 'ai_insight' && (
                                        <Badge variant="secondary" className="text-xs mb-2">
                                          <Sparkles className="w-3 h-3 mr-1" />
                                          AI Insight
                                        </Badge>
                                      )}
                                      <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                                      <p className="text-xs text-muted-foreground mt-2">
                                        {formatNoteDate(note.created_at)}
                                      </p>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                                      onClick={() => deleteNote(note.id)}
                                      data-testid={`delete-note-${note.id}`}
                                    >
                                      <Trash2 className="w-3 h-3 text-destructive" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Daily Costs View */}
      {view === 'costs' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Projected Labour Costs</CardTitle>
                <CardDescription>
                  {format(currentWeekStart, 'd MMM')} - {format(weekEnd, 'd MMM yyyy')}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={prevWeek}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={goToToday}>
                  Today
                </Button>
                <Button variant="outline" size="icon" onClick={nextWeek}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-center">Shifts</TableHead>
                    <TableHead className="text-center">Hours</TableHead>
                    <TableHead className="text-right">Projected Cost</TableHead>
                    <TableHead>Staff On</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {weekDates.map(date => {
                    const costData = getDailyCostForDate(date);
                    return (
                      <TableRow 
                        key={date.toISOString()} 
                        className={`table-row-hover ${isToday(date) ? 'bg-primary/5' : ''}`}
                      >
                        <TableCell>
                          <div className="font-medium">{format(date, 'EEEE')}</div>
                          <div className="text-sm text-muted-foreground">{format(date, 'd MMM')}</div>
                        </TableCell>
                        <TableCell className="text-center">
                          {costData?.shifts_scheduled || 0}
                        </TableCell>
                        <TableCell className="text-center mono-num">
                          {costData?.total_hours?.toFixed(1) || '0.0'}
                        </TableCell>
                        <TableCell className="text-right mono-num font-medium text-loss">
                          {formatCurrency(costData?.projected_labor_cost || 0)}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {costData?.staff_on?.slice(0, 3).map((name, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {name}
                              </Badge>
                            ))}
                            {costData?.staff_on?.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{costData.staff_on.length - 3}
                              </Badge>
                            )}
                            {!costData?.staff_on?.length && (
                              <span className="text-muted-foreground text-sm">No shifts</span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Week total row */}
                  <TableRow className="bg-muted/50 font-medium">
                    <TableCell>Week Total</TableCell>
                    <TableCell className="text-center">{weekTotalShifts}</TableCell>
                    <TableCell className="text-center mono-num">{weekTotalHours.toFixed(1)}</TableCell>
                    <TableCell className="text-right mono-num text-loss">{formatCurrency(weekTotalCost)}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payments View - Now shows actual hours from Toast imports */}
      {view === 'payments' && (
        <div className="space-y-4">
          {/* Upload Reminder Alert */}
          {hoursDashboard?.upload_overdue && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  <div className="flex-1">
                    <p className="font-medium text-amber-800">
                      {hoursDashboard.last_upload_date 
                        ? `Time Entry data is ${hoursDashboard.days_since_last_period} days old`
                        : 'No Time Entry data uploaded yet'}
                    </p>
                    <p className="text-sm text-amber-700">
                      Upload your weekly POS time-entry export to see actual hours worked
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-amber-400 text-amber-700 hover:bg-amber-100"
                    onClick={() => window.location.href = '/expenses'}
                  >
                    Go to Expenses
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Hours Dashboard Card */}
          <Card>
            <CardHeader className="pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle>Actual Hours Worked</CardTitle>
                  <CardDescription>
                    From your POS time-entry imports • Wages appear as expenses you can pay
                  </CardDescription>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {hoursDashboard?.totals && (
                    <div className="text-left sm:text-right mr-0 sm:mr-4">
                      <div className="text-sm text-muted-foreground">Total Wages (Period)</div>
                      <div className="text-xl font-bold text-loss">
                        {formatCurrency(hoursDashboard.totals.total_pay)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingHours ? (
                <div className="py-12 text-center">
                  <Skeleton className="h-8 w-48 mx-auto mb-4" />
                  <Skeleton className="h-48 w-full" />
                </div>
              ) : !hoursDashboard?.employees?.length ? (
                <div className="py-12 text-center">
                  <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">No Time Entry Data</h3>
                  <p className="text-muted-foreground mb-4">
                    Upload your POS time-entry export from the Expenses page
                  </p>
                  <Button onClick={() => window.location.href = '/expenses'}>
                    Go to Expenses
                  </Button>
                </div>
              ) : (
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[140px]">Employee</TableHead>
                        <TableHead className="text-center min-w-[80px]">Regular Hrs</TableHead>
                        <TableHead className="text-center min-w-[80px]">Overtime</TableHead>
                        <TableHead className="text-right min-w-[80px]">Rate</TableHead>
                        <TableHead className="text-right min-w-[80px]">Total Pay</TableHead>
                        <TableHead className="text-right min-w-[60px]">Tips</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {hoursDashboard.employees.map((emp, idx) => (
                        <TableRow key={idx} className="table-row-hover">
                          <TableCell>
                            <div className="font-medium">{emp.employee_name}</div>
                            {emp.job_title && (
                              <Badge variant="secondary" className="text-xs">{emp.job_title}</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-center mono-num">
                            {emp.total_regular_hours.toFixed(1)}
                          </TableCell>
                          <TableCell className="text-center mono-num">
                            {emp.total_overtime_hours > 0 ? (
                              <span className="text-amber-600">{emp.total_overtime_hours.toFixed(1)}</span>
                            ) : '-'}
                          </TableCell>
                          <TableCell className="text-right mono-num">
                            {formatCurrency(emp.hourly_rate)}/hr
                          </TableCell>
                          <TableCell className="text-right mono-num font-medium text-loss">
                            {formatCurrency(emp.total_pay)}
                          </TableCell>
                          <TableCell className="text-right mono-num text-profit">
                            {emp.total_tips > 0 ? formatCurrency(emp.total_tips) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Totals row */}
                      <TableRow className="bg-muted/50 font-medium">
                        <TableCell>Total ({hoursDashboard.totals.employee_count} employees)</TableCell>
                        <TableCell className="text-center mono-num">{hoursDashboard.totals.total_regular_hours.toFixed(1)}</TableCell>
                        <TableCell className="text-center mono-num text-amber-600">
                          {hoursDashboard.totals.total_overtime_hours > 0 ? hoursDashboard.totals.total_overtime_hours.toFixed(1) : '-'}
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell className="text-right mono-num font-bold text-loss">{formatCurrency(hoursDashboard.totals.total_pay)}</TableCell>
                        <TableCell className="text-right mono-num text-profit">
                          {hoursDashboard.totals.total_tips > 0 ? formatCurrency(hoursDashboard.totals.total_tips) : '-'}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              )}
              
              {/* Period info */}
              {hoursDashboard?.periods_covered?.length > 0 && (
                <div className="mt-4 text-sm text-muted-foreground">
                  <span className="font-medium">Data from:</span>{' '}
                  {hoursDashboard.periods_covered.map(([start, end], idx) => (
                    <span key={idx}>
                      {idx > 0 && ', '}
                      {start} to {end}
                    </span>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Staff Debt/Adjustments Section */}
          <Card>
            <CardHeader className="pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle>Staff Debt & Adjustments</CardTitle>
                  <CardDescription>
                    Track unpaid wages, overtime owed, or other adjustments
                  </CardDescription>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {paymentSummary?.totals && paymentSummary.totals.total_balance_owed !== 0 && (
                    <div className="text-left sm:text-right mr-0 sm:mr-4">
                      <div className="text-sm text-muted-foreground">Outstanding Debt</div>
                      <div className={`text-xl font-bold ${paymentSummary.totals.total_balance_owed > 0 ? 'text-loss' : 'text-profit'}`}>
                        {formatCurrency(paymentSummary.totals.total_balance_owed)}
                      </div>
                    </div>
                  )}
                  <Button
                    variant={showInactive ? "default" : "outline"}
                    size="sm"
                    onClick={() => setShowInactive(!showInactive)}
                  >
                    <Filter className="w-4 h-4 mr-2" />
                    {showInactive ? 'All' : 'Active'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {!paymentSummary ? (
                <div className="py-8 text-center">
                  <Skeleton className="h-32 w-full" />
                </div>
              ) : (
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[140px]">Employee</TableHead>
                        <TableHead className="text-right min-w-[90px]">Historic Debt</TableHead>
                        <TableHead className="text-right min-w-[70px]">Paid</TableHead>
                        <TableHead className="text-right min-w-[100px]">Balance</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paymentSummary.employees
                        .filter(emp => emp.historic_debt > 0 || emp.total_paid > 0 || emp.balance_owed !== 0)
                        .map(emp => (
                        <TableRow key={emp.employee_id} className="table-row-hover">
                          <TableCell>
                            <div className="font-medium">{emp.employee_name}</div>
                            {emp.role && <Badge variant="secondary" className="text-xs">{emp.role}</Badge>}
                          </TableCell>
                          <TableCell className="text-right mono-num">
                            {emp.historic_debt > 0 ? (
                              <span className="text-orange-600">+{formatCurrency(emp.historic_debt)}</span>
                            ) : '-'}
                          </TableCell>
                          <TableCell className="text-right mono-num text-profit">
                            {emp.total_paid > 0 ? formatCurrency(emp.total_paid) : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={`mono-num font-semibold ${emp.balance_owed > 0 ? 'text-loss' : emp.balance_owed < 0 ? 'text-profit' : ''}`}>
                              {formatCurrency(emp.balance_owed)}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openTransactionHistory(emp)}
                                title="View & edit transaction history"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openDebtDialog(emp)}
                                title="Add historic debt"
                              >
                                <History className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {paymentSummary.employees.filter(emp => emp.historic_debt > 0 || emp.total_paid > 0 || emp.balance_owed !== 0).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                            No debt or adjustments recorded. Use &quot;Add Debt&quot; to record unpaid wages or overtime owed.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
              
              {/* Quick add debt for any employee */}
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-muted-foreground mb-2">Record debt for an employee:</p>
                <div className="flex flex-wrap gap-2">
                  {employees.filter(e => !e.status || e.status === 'active').slice(0, 8).map(emp => (
                    <Button
                      key={emp.id}
                      variant="outline"
                      size="sm"
                      onClick={() => openDebtDialog({ employee_id: emp.id, employee_name: emp.name })}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      {emp.name.split(',')[0]}
                    </Button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Shift Dialog */}
      <Dialog open={shiftDialogOpen} onOpenChange={setShiftDialogOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingShift ? 'Edit Shift' : 'Add Shift'}</DialogTitle>
            <DialogDescription>
              {editingShift ? 'Update shift details' : 'Schedule a new shift for an employee'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Employee</Label>
              <Select 
                value={shiftForm.employee_id} 
                onValueChange={(v) => setShiftForm({...shiftForm, employee_id: v})}
                disabled={!!editingShift}
              >
                <SelectTrigger data-testid="shift-employee-select">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    disabled={!!editingShift}
                    data-testid="shift-date-btn"
                  >
                    <CalendarDays className="w-4 h-4 mr-2" />
                    {shiftForm.date ? format(parseISO(shiftForm.date), 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={shiftForm.date ? parseISO(shiftForm.date) : undefined}
                    onSelect={(date) => date && setShiftForm({...shiftForm, date: format(date, 'yyyy-MM-dd')})}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Input
                  type="time"
                  value={shiftForm.start_time}
                  onChange={(e) => setShiftForm({...shiftForm, start_time: e.target.value})}
                  data-testid="shift-start-time"
                />
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <Input
                  type="time"
                  value={shiftForm.end_time}
                  onChange={(e) => setShiftForm({...shiftForm, end_time: e.target.value})}
                  data-testid="shift-end-time"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Break (minutes)</Label>
              <Input
                type="number"
                min="0"
                value={shiftForm.break_minutes}
                onChange={(e) => setShiftForm({...shiftForm, break_minutes: e.target.value})}
                placeholder="30"
                data-testid="shift-break-minutes"
              />
            </div>

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input
                value={shiftForm.notes}
                onChange={(e) => setShiftForm({...shiftForm, notes: e.target.value})}
                placeholder="Any special instructions..."
                data-testid="shift-notes"
              />
            </div>

            {/* Repeat options - only for new shifts */}
            {!editingShift && (
              <div className="space-y-3 pt-2 border-t">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="repeat-shift"
                    checked={shiftForm.repeat}
                    onCheckedChange={(checked) => setShiftForm({...shiftForm, repeat: checked})}
                    data-testid="shift-repeat-checkbox"
                  />
                  <Label htmlFor="repeat-shift" className="flex items-center gap-2 cursor-pointer">
                    <Repeat className="w-4 h-4" />
                    Repeat this shift weekly
                  </Label>
                </div>
                
                {shiftForm.repeat && (
                  <div className="ml-6 space-y-2">
                    <Label className="text-sm">For how many weeks?</Label>
                    <Select 
                      value={shiftForm.repeat_weeks.toString()} 
                      onValueChange={(v) => setShiftForm({...shiftForm, repeat_weeks: parseInt(v)})}
                    >
                      <SelectTrigger className="w-32" data-testid="repeat-weeks-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2">2 weeks</SelectItem>
                        <SelectItem value="4">4 weeks</SelectItem>
                        <SelectItem value="6">6 weeks</SelectItem>
                        <SelectItem value="8">8 weeks</SelectItem>
                        <SelectItem value="12">12 weeks</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Creates {shiftForm.repeat_weeks} shifts on the same day each week
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="flex gap-2">
            {editingShift && (
              <Button 
                variant="destructive" 
                onClick={() => {
                  setShiftDialogOpen(false);
                  confirmDeleteShift(editingShift);
                }}
                data-testid="delete-shift-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            )}
            <Button variant="outline" onClick={() => setShiftDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveShift} data-testid="save-shift-btn">
              {editingShift ? 'Update' : 'Add'} Shift
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employee Dialog */}
      <Dialog open={employeeDialogOpen} onOpenChange={setEmployeeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingEmployee ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
            <DialogDescription>
              {editingEmployee ? 'Update employee details' : 'Add a new team member'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={employeeForm.name}
                onChange={(e) => setEmployeeForm({...employeeForm, name: e.target.value})}
                placeholder="John Smith"
                data-testid="employee-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Role</Label>
              <Input
                value={employeeForm.role}
                onChange={(e) => setEmployeeForm({...employeeForm, role: e.target.value})}
                placeholder="Barista, Shift Manager, etc."
                data-testid="employee-role-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Hourly Rate (£)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={employeeForm.hourly_rate}
                onChange={(e) => setEmployeeForm({...employeeForm, hourly_rate: e.target.value})}
                placeholder="12.50"
                data-testid="employee-rate-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmployeeDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveEmployee} data-testid="save-employee-btn">
              {editingEmployee ? 'Update' : 'Add'} Employee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              Delete Shift
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this shift? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={deleteShift} data-testid="confirm-delete-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="w-5 h-5" />
              Add Note for {selectedEmployeeForNote?.name}
            </DialogTitle>
            <DialogDescription>
              Save an observation, insight, or reminder about this team member
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Note Content</Label>
              <Textarea
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                placeholder="E.g., Great performance on Saturday shift, handled rush hour well..."
                rows={4}
                data-testid="note-content-input"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Tip: Ask the AI Advisor questions like &ldquo;How is {selectedEmployeeForNote?.name} performing?&rdquo; 
              to get insights you can save here.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveNote} data-testid="save-note-btn">
              <StickyNote className="w-4 h-4 mr-2" />
              Save Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employee Status Change Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserMinus className="w-5 h-5 text-orange-500" />
              Remove {selectedEmployeeForStatus?.name}
            </DialogTitle>
            <DialogDescription>
              Choose how to handle this employee&apos;s record
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <div 
                className={`p-4 border rounded-lg cursor-pointer transition-colors ${statusForm.status === 'left' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}
                onClick={() => setStatusForm({...statusForm, status: 'left'})}
                data-testid="status-left-option"
              >
                <div className="flex items-center gap-3">
                  <input 
                    type="radio" 
                    checked={statusForm.status === 'left'}
                    onChange={() => setStatusForm({...statusForm, status: 'left'})}
                    className="w-4 h-4"
                  />
                  <div>
                    <div className="font-medium">Left Company</div>
                    <div className="text-sm text-muted-foreground">
                      Mark as no longer employed. Historical data (payroll, orders) will be preserved.
                    </div>
                  </div>
                </div>
                {statusForm.status === 'left' && (
                  <div className="mt-3 ml-7">
                    <Label className="text-sm">Leaving Date</Label>
                    <Input
                      type="date"
                      value={statusForm.left_date}
                      onChange={(e) => setStatusForm({...statusForm, left_date: e.target.value})}
                      className="mt-1"
                      data-testid="left-date-input"
                    />
                  </div>
                )}
              </div>
              
              <div 
                className={`p-4 border rounded-lg cursor-pointer transition-colors ${statusForm.status === 'archived' ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`}
                onClick={() => setStatusForm({...statusForm, status: 'archived'})}
                data-testid="status-archived-option"
              >
                <div className="flex items-center gap-3">
                  <input 
                    type="radio" 
                    checked={statusForm.status === 'archived'}
                    onChange={() => setStatusForm({...statusForm, status: 'archived'})}
                    className="w-4 h-4"
                  />
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      <Archive className="w-4 h-4" />
                      Archive
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Hide from active lists but keep all data. Good for demo staff you want to hide.
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <p className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-lg">
              You can reactivate this employee at any time from the inactive list, or delete them permanently if they were demo data.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
            <Button onClick={changeEmployeeStatus} data-testid="confirm-status-btn">
              {statusForm.status === 'left' ? 'Mark as Left' : 'Archive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Employee Permanently Dialog */}
      <Dialog open={deleteEmployeeDialogOpen} onOpenChange={setDeleteEmployeeDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              Delete Permanently
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete <strong>{employeeToDelete?.name}</strong>?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 text-sm">
              <p className="font-medium text-destructive mb-2">This will:</p>
              <ul className="list-disc ml-4 space-y-1 text-muted-foreground">
                <li>Remove the employee record completely</li>
                <li>Delete all notes attached to this employee</li>
                <li>Remove any future scheduled shifts</li>
              </ul>
              <p className="mt-2 text-muted-foreground">
                Historical data (past payroll, completed orders) will remain.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteEmployeeDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={deleteEmployeePermanently} data-testid="confirm-delete-employee-btn">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Record Payment
            </DialogTitle>
            <DialogDescription>
              Record a payment made to {selectedEmployeeForPayment?.employee_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedEmployeeForPayment && (
              <div className="bg-muted/50 rounded-lg p-3 text-sm">
                <div className="flex justify-between">
                  <span>Balance Owed:</span>
                  <span className={`font-semibold ${selectedEmployeeForPayment.balance_owed > 0 ? 'text-loss' : 'text-profit'}`}>
                    {formatCurrency(selectedEmployeeForPayment.balance_owed)}
                  </span>
                </div>
                {selectedEmployeeForPayment.is_hourly && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>Hours worked:</span>
                    <span>{selectedEmployeeForPayment.total_hours_worked}h @ {formatCurrency(selectedEmployeeForPayment.hourly_rate)}/hr</span>
                  </div>
                )}
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Amount (£)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                placeholder="0.00"
                data-testid="payment-amount-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={paymentForm.payment_date}
                onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})}
                data-testid="payment-date-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                placeholder="e.g., Bank transfer, Cash"
                data-testid="payment-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
            <Button onClick={recordPayment} data-testid="record-payment-btn">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Historic Debt Dialog */}
      <Dialog open={debtDialogOpen} onOpenChange={setDebtDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Add Historic Debt
            </DialogTitle>
            <DialogDescription>
              Add pre-existing amount owed to {selectedEmployeeForPayment?.employee_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              Use this to add any money owed to this employee from before you started using this system.
            </div>
            
            <div className="space-y-2">
              <Label>Amount Owed (£)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={debtForm.amount}
                onChange={(e) => setDebtForm({...debtForm, amount: e.target.value})}
                placeholder="0.00"
                data-testid="debt-amount-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={debtForm.description}
                onChange={(e) => setDebtForm({...debtForm, description: e.target.value})}
                placeholder="e.g., Unpaid wages from December"
                data-testid="debt-description-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Date Incurred (optional)</Label>
              <Input
                type="date"
                value={debtForm.date_incurred}
                onChange={(e) => setDebtForm({...debtForm, date_incurred: e.target.value})}
                data-testid="debt-date-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDebtDialogOpen(false)}>Cancel</Button>
            <Button onClick={addHistoricDebt} data-testid="add-debt-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Debt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transaction History Dialog */}
      <Dialog open={transactionHistoryOpen} onOpenChange={setTransactionHistoryOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Transaction History</DialogTitle>
            <DialogDescription>
              {selectedEmployeeForPayment?.employee_name} - View and manage all payments and debts
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {loadingTransactions ? (
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : selectedEmployeeTransactions.length === 0 ? (
              <div className="py-8 text-center">
                <History className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">No transactions</h3>
                <p className="text-muted-foreground">No payments or debts recorded for this employee</p>
              </div>
            ) : (
              <ScrollArea className="max-h-[400px]">
                <div className="space-y-2">
                  {selectedEmployeeTransactions.map(trans => (
                    <div 
                      key={trans.id} 
                      className={`flex items-center justify-between p-3 rounded-md border ${
                        trans.type === 'payment' ? 'bg-green-50/50 border-green-200' : 
                        trans.type === 'historic_debt' ? 'bg-orange-50/50 border-orange-200' :
                        'bg-blue-50/50 border-blue-200'
                      }`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Badge variant={trans.type === 'payment' ? 'default' : trans.type === 'historic_debt' ? 'secondary' : 'outline'}>
                            {trans.type === 'payment' ? 'Payment' : 
                             trans.type === 'historic_debt' ? 'Historic Debt' : 
                             'Adjustment'}
                          </Badge>
                          <span className={`font-mono font-semibold ${
                            trans.type === 'payment' ? 'text-green-700' : 'text-orange-700'
                          }`}>
                            {trans.type === 'payment' ? '-' : '+'}£{trans.amount.toFixed(2)}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {trans.type === 'payment' ? (
                            <>Paid on {format(parseISO(trans.payment_date || trans.created_at), 'dd MMM yyyy')}</>
                          ) : trans.type === 'historic_debt' ? (
                            <>Incurred: {trans.date_incurred ? format(parseISO(trans.date_incurred), 'dd MMM yyyy') : 'N/A'}</>
                          ) : (
                            <>Date: {format(parseISO(trans.created_at), 'dd MMM yyyy')}</>
                          )}
                          {trans.description && <> • {trans.description}</>}
                          {trans.notes && <> • {trans.notes}</>}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:bg-destructive/10"
                        onClick={() => handleDeleteTransaction(trans.id)}
                        title="Delete this transaction"
                        data-testid={`delete-trans-${trans.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
            
            {/* Summary */}
            {selectedEmployeeForPayment && (
              <div className="mt-4 pt-4 border-t space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Historic Debt:</span>
                  <span className="font-mono text-orange-600">
                    +£{selectedEmployeeForPayment.historic_debt?.toFixed(2) || '0.00'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Paid:</span>
                  <span className="font-mono text-green-600">
                    -£{selectedEmployeeForPayment.total_paid?.toFixed(2) || '0.00'}
                  </span>
                </div>
                <div className="flex justify-between font-medium pt-2 border-t">
                  <span>Current Balance:</span>
                  <span className={`font-mono ${selectedEmployeeForPayment.balance_owed > 0 ? 'text-loss' : 'text-profit'}`}>
                    £{selectedEmployeeForPayment.balance_owed?.toFixed(2) || '0.00'}
                  </span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setTransactionHistoryOpen(false)}>
              Close
            </Button>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => {
                setTransactionHistoryOpen(false);
                openDebtDialog(selectedEmployeeForPayment);
              }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Debt
              </Button>
              <Button onClick={() => {
                setTransactionHistoryOpen(false);
                openPaymentDialog(selectedEmployeeForPayment);
              }} disabled={!selectedEmployeeForPayment || selectedEmployeeForPayment.balance_owed <= 0}>
                <CreditCard className="w-4 h-4 mr-2" />
                Record Payment
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffPage;
